'use client';
import { omit } from 'lodash';
import React, { useCallback, useState, useEffect } from 'react';
import { Case, Default, Switch, When } from 'react-if';
import { usePathname } from 'next/navigation';

import { Box } from '@nerdwallet/currency/components/Box';
import { Button } from '@nerdwallet/currency/components/Button';
import { Link } from '@nerdwallet/currency/components/Link';
// eslint-disable-next-line no-restricted-imports
import { Modal } from '@nerdwallet/currency/components/Modal';
import { Stack } from '@nerdwallet/currency/components/Stack';
import { Text } from '@nerdwallet/currency/components/Text';
import { Title } from '@nerdwallet/currency/components/Title';

import {
  hideModal as hideModalAction,
  updateModal as updateModalAction,
} from '@lib/redux/actions/modals';
import { useAppDispatch, useAppSelector } from '@lib/hooks/redux-hooks';

import MODAL_CONFIGS from './modals';
import { getActiveModals } from './util';
import LegacyModal from './legacy-modal/legacy-modal';
import { usePrevious } from '@lib/utils';

const modalAnimateMS = 300;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

interface CloseConfirmationProps {
  cancelLabel?: string;
  confirmLabel?: string;
  description?: string;
  onBack: () => void;
  onContinue: () => void;
  title?: string;
}

const CloseConfirmation = ({
  cancelLabel = 'No, go back',
  confirmLabel = 'Yes, close',
  description,
  onBack,
  onContinue,
  title = 'Are you sure you want to close?',
}: CloseConfirmationProps) => {
  return (
    <Stack spacing={5}>
      <Stack spacing={3}>
        <Title size="small">{title}</Title>
        <When condition={description}>
          <Text>{description}</Text>
        </When>
      </Stack>

      <Stack spacing={3}>
        <Button block primary onClick={onContinue}>
          {confirmLabel}
        </Button>
        <Text align="center" component="p">
          <Link onClick={onBack}>{cancelLabel}</Link>
        </Text>
      </Stack>
    </Stack>
  );
};

interface ModalInstanceProps {
  Component: JSX.ElementType;
  config: {
    // Props for configuring the Close Confirmation screen
    confirmationCancelLabel?: string;
    confirmationConfirmLabel?: string;
    confirmationDescription?: string;
    confirmationTitle?: string;
    // Whether the "Are you sure?" modal dismiss screen is visible
    isConfirmationVisible?: boolean;
    // Callback when the modal fully closes
    onClose?: () => void;
    // Callback when the user manually dismisses modal (by clicking the [x] or the overlay)
    onNativeClose?: () => void;
    // Callback if user tries to push a modal's "Back" button
    onRequestBack?: () => void;
    // Whether to show a confirmation if a user tries to close the modal
    shouldPromptOnClose?: boolean;
    // Whether to show the back button in the modal
    shouldShowBack?: boolean;

    // Other configurable @nerdwallet/currency/modal props
    backgroundColor?: string;
    width?: string;
    // Close the modal when location changes
    closeModalWhenLocationChanges?: boolean;
    // Other configurable @nerdwallet/react-modal props
    additionalClassName?: string;
    hasScrollLock?: boolean;
    isScrollable?: boolean;
    shouldShowSecurityMessage?: boolean;
    size?: string;
    subtitle?: string;
    title?: string;

    // DEPRECATED: Config options for legacy modal only
    allowUserDismiss?: boolean;
    legacyModal?: boolean;
    onAfterDismiss?: () => void;
    useWhiteCloseButton?: boolean;
  };
  hideModal: (string) => void;
  isOpen: boolean;
  modalType: string;
  props: any;
  removeModal: () => void;
  updateModal: (modalType: string, props: any, config: any) => void;
}

const ModalInstance = ({
  Component,
  config: {
    confirmationCancelLabel,
    confirmationConfirmLabel,
    confirmationDescription,
    confirmationTitle,
    isConfirmationVisible,
    onClose,
    onNativeClose,
    onRequestBack,
    shouldPromptOnClose,
    shouldShowBack,

    // Other configurable @nerdwallet/currency/modal props
    backgroundColor,
    width,
    closeModalWhenLocationChanges,

    // Less common @nerdwallet/react-modal props
    additionalClassName,
    isScrollable,
    hasScrollLock,
    shouldShowSecurityMessage,
    subtitle,
    title,

    // DEPRECATED: Config options for legacy modal only
    allowUserDismiss,
    legacyModal,
    onAfterDismiss,
    size,
    useWhiteCloseButton,
  },
  hideModal,
  isOpen,
  modalType,
  props,
  removeModal,
  updateModal,
}: ModalInstanceProps) => {
  const pathname = usePathname();
  const previousPathname = usePrevious(pathname);

  const handleHideModal = useCallback(
    async (animateDelay = 0) => {
      // Remove modal from list of active modals
      removeModal();

      // Wait for modal's "closing" animation to finish
      await sleep(animateDelay);

      // Remove modal from Redux store
      hideModal(modalType);

      // Hide confirmation messaging only after fully closing, if needed
      updateModal(modalType, null, { isConfirmationVisible: false });

      // Execute callback for modal only after fully closing
      if (onClose) {
        onClose();
      }
    },
    [hideModal, modalType, onClose, removeModal, updateModal],
  );

  const handleRequestClose = (animateDelay) => {
    // If user tries to close the modal while *already* viewing the
    // exit confirmation message, then just allow user to close the modal
    if (shouldPromptOnClose && !isConfirmationVisible) {
      updateModal(modalType, null, { isConfirmationVisible: true });
      return;
    }

    // This callback executes only if the user closes the modal via clicking the [x] or the overlay.
    if (onNativeClose) {
      onNativeClose();
    }

    handleHideModal(animateDelay);
  };

  const handleCancelRequestClose = () => {
    updateModal(modalType, null, { isConfirmationVisible: false });
  };

  const handleBackClick = () => {
    if (onRequestBack) {
      onRequestBack();
    }
  };

  const onLegacyAttemptDismiss = () => {
    handleRequestClose(0);
    if (onAfterDismiss) {
      onAfterDismiss();
    }
  };

  const handleUpdateModal = useCallback(
    (updateProps, updateConfig) => {
      updateModal(modalType, updateProps, updateConfig);
    },
    [modalType, updateModal],
  );

  useEffect(() => {
    if (closeModalWhenLocationChanges && pathname !== previousPathname) {
      handleHideModal();
    }
  }, [
    closeModalWhenLocationChanges,
    pathname,
    previousPathname,
    handleHideModal,
  ]);

  return (
    <Switch>
      <Case condition={legacyModal}>
        <LegacyModal
          additionalClassName={additionalClassName}
          allowUserDismiss={allowUserDismiss}
          key={modalType}
          onAttemptDismiss={onLegacyAttemptDismiss}
          size={size}
          useWhiteCloseButton={useWhiteCloseButton}
        >
          <Box display={isConfirmationVisible ? 'block' : 'none'} padding={4}>
            <CloseConfirmation
              cancelLabel={confirmationCancelLabel}
              confirmLabel={confirmationConfirmLabel}
              description={confirmationDescription}
              onBack={handleCancelRequestClose}
              onContinue={onLegacyAttemptDismiss}
              title={confirmationTitle}
            />
          </Box>

          <Box display={isConfirmationVisible ? 'none' : 'block'}>
            {Component && (
              <Component
                {...props}
                hideModal={handleHideModal}
                updateModal={handleUpdateModal}
              />
            )}
          </Box>
        </LegacyModal>
      </Case>
      <Default>
        <Modal
          animateMS={modalAnimateMS}
          className={additionalClassName}
          hasScrollLock={hasScrollLock}
          id={modalType}
          isOpen={isOpen}
          isScrollable={isScrollable}
          key={modalType}
          onRequestBack={handleBackClick}
          onRequestClose={() => handleRequestClose(modalAnimateMS)}
          shouldShowBack={isConfirmationVisible ? false : shouldShowBack}
          shouldShowSecurityMessage={shouldShowSecurityMessage}
          subtitle={subtitle}
          title={title}
          backgroundColor={backgroundColor}
          width={width}
        >
          <Box display={isConfirmationVisible ? 'block' : 'none'}>
            <CloseConfirmation
              cancelLabel={confirmationCancelLabel}
              confirmLabel={confirmationConfirmLabel}
              description={confirmationDescription}
              onBack={handleCancelRequestClose}
              onContinue={() => handleRequestClose(modalAnimateMS)}
              title={confirmationTitle}
            />
          </Box>

          <Box display={isConfirmationVisible ? 'none' : 'block'}>
            {Component && (
              <Component
                {...props}
                hideModal={() => handleHideModal(modalAnimateMS)}
                updateModal={handleUpdateModal}
              />
            )}
          </Box>
        </Modal>
      </Default>
    </Switch>
  );
};

export const ModalManager = () => {
  const dispatch = useAppDispatch();
  const modals = useAppSelector(getActiveModals);
  const [activeModals, setActiveModals] = useState({});

  const hideModal = useCallback(
    (modalType) => {
      dispatch(hideModalAction(modalType));
    },
    [dispatch],
  );

  const updateModal = useCallback(
    (modalType, props, config) => {
      dispatch(updateModalAction({ modalType, props, config }));
    },
    [dispatch],
  );

  useEffect(
    // Copy `modals` to `activeModals`.
    // The "active modal" list lets us make sure the modal closing animation is smooth.
    // Without this, the modal contents disappear from the UI before the modal is fully closed.
    function updateActiveModals() {
      setActiveModals(modals);
    },
    [modals],
  );
  return (
    // Initialize all possible modals ahead of time so they can smoothly animate open.
    Object.keys(MODAL_CONFIGS).map((modalType) => {
      const { Component, props, modalConfig = {} } = modals[modalType] || {};

      return (
        <ModalInstance
          Component={Component}
          config={modalConfig}
          hideModal={hideModal}
          isOpen={!!activeModals[modalType]}
          key={modalType}
          modalType={modalType}
          props={props}
          removeModal={() => setActiveModals(omit(activeModals, modalType))}
          updateModal={updateModal}
        />
      );
    })
  );
};

export default ModalManager;
